import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useQuery } from '@tanstack/react-query'
import { committentiApi } from '../services/api'
import {
  rapportinoTemplatesApi,
  type RapportinoTemplate,
  type CampoTemplate,
  type TemplateListItem,
} from '../services/rapportinoTemplates'
import './RapportinoTemplateEditor.css'

// ── TIPI CAMPO DISPONIBILI ────────────────────────────────────────────────────
const TIPI_CAMPO = [
  { tipo: 'testo',      icona: '📝', label: 'Testo breve' },
  { tipo: 'textarea',   icona: '📄', label: 'Testo lungo' },
  { tipo: 'numero',     icona: '🔢', label: 'Numero' },
  { tipo: 'data',       icona: '📅', label: 'Data' },
  { tipo: 'ora',        icona: '🕐', label: 'Ora' },
  { tipo: 'checkbox',   icona: '☑️',  label: 'Checkbox' },
  { tipo: 'select',     icona: '🔽', label: 'Selezione' },
  { tipo: 'foto',       icona: '📷', label: 'Foto' },
  { tipo: 'firma',      icona: '✍️',  label: 'Firma' },
  { tipo: 'materiali',  icona: '📦', label: 'Materiali/Kit' },
  { tipo: 'separatore', icona: '➖', label: 'Separatore' },
] as const

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
}

function newCampo(tipo: CampoTemplate['tipo'], ordine: number): CampoTemplate {
  return {
    id: uid(),
    tipo,
    etichetta: TIPI_CAMPO.find(t => t.tipo === tipo)?.label ?? tipo,
    obbligatorio: false,
    ordine,
    larghezza: 'full',
    opzioni: tipo === 'select' ? ['Opzione 1', 'Opzione 2'] : undefined,
    max_foto: tipo === 'foto' ? 4 : undefined,
  }
}

// ── COMPONENTE PRINCIPALE ─────────────────────────────────────────────────────
export default function RapportinoTemplateEditor() {
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [template, setTemplate] = useState<RapportinoTemplate | null>(null)
  const [selectedCampoId, setSelectedCampoId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [tab, setTab] = useState<'editor' | 'anteprima'>('editor')

  const { data: committentiRaw } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data?.items ?? r.data ?? []),
  })
  const committenti: Array<{ id: number; ragione_sociale: string }> = Array.isArray(committentiRaw) ? committentiRaw : []

  const loadTemplates = useCallback(async () => {
    try {
      const raw = await rapportinoTemplatesApi.list()
      const list = Array.isArray(raw) ? raw : (raw as any)?.items ?? []
      setTemplates(list)
    } catch {
      toast.error('Errore nel caricamento dei template')
    }
  }, [])

  useEffect(() => { loadTemplates() }, [loadTemplates])

  const selectTemplate = async (id: number) => {
    if (dirty && !window.confirm('Hai modifiche non salvate. Continuare?')) return
    setLoading(true)
    try {
      const t = await rapportinoTemplatesApi.get(id)
      t.campi = t.campi.map((c, i) => ({ ...c, ordine: i }))
      setTemplate(t)
      setSelectedId(id)
      setSelectedCampoId(null)
      setDirty(false)
      setTab('editor')
    } catch {
      toast.error('Errore nel caricamento del template')
    } finally {
      setLoading(false)
    }
  }

  const nuovoTemplate = () => {
    if (dirty && !window.confirm('Hai modifiche non salvate. Continuare?')) return
    setTemplate({
      id: 0,
      nome: 'Nuovo template',
      descrizione: '',
      committente_id: null,
      campi: [],
      versione: 1,
      attivo: true,
    })
    setSelectedId(null)
    setSelectedCampoId(null)
    setDirty(true)
    setTab('editor')
  }

  // ── GESTIONE CAMPI ──────────────────────────────────────────────────────────

  const aggiungiCampo = (tipo: CampoTemplate['tipo']) => {
    if (!template) return
    const nuovo = newCampo(tipo, template.campi.length)
    setTemplate(t => t ? { ...t, campi: [...t.campi, nuovo] } : t)
    setSelectedCampoId(nuovo.id)
    setDirty(true)
  }

  const rimuoviCampo = (id: string) => {
    setTemplate(t => t ? {
      ...t,
      campi: t.campi.filter(c => c.id !== id).map((c, i) => ({ ...c, ordine: i })),
    } : t)
    if (selectedCampoId === id) setSelectedCampoId(null)
    setDirty(true)
  }

  const updateCampo = (id: string, patch: Partial<CampoTemplate>) => {
    setTemplate(t => t ? {
      ...t,
      campi: t.campi.map(c => c.id === id ? { ...c, ...patch } : c),
    } : t)
    setDirty(true)
  }

  // ── DRAG AND DROP ───────────────────────────────────────────────────────────

  const handleDragStart = (index: number) => setDraggedIndex(index)
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }
  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }
    setTemplate(t => {
      if (!t) return t
      const campi = [...t.campi]
      const [moved] = campi.splice(draggedIndex, 1)
      campi.splice(targetIndex, 0, moved)
      return { ...t, campi: campi.map((c, i) => ({ ...c, ordine: i })) }
    })
    setDirty(true)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  // ── SALVATAGGIO ─────────────────────────────────────────────────────────────

  const salva = async () => {
    if (!template) return
    setSaving(true)
    try {
      const payload = {
        nome: template.nome,
        descrizione: template.descrizione,
        committente_id: template.committente_id,
        campi: template.campi,
        attivo: template.attivo,
      }
      const saved = template.id === 0
        ? await rapportinoTemplatesApi.create(payload)
        : await rapportinoTemplatesApi.update(template.id, payload)
      setTemplate(saved)
      setSelectedId(saved.id)
      setDirty(false)
      await loadTemplates()
      toast.success('Template salvato')
    } catch {
      toast.error('Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const duplica = async (id: number) => {
    try {
      await rapportinoTemplatesApi.duplica(id)
      await loadTemplates()
      toast.success('Template duplicato')
    } catch {
      toast.error('Errore nella duplicazione')
    }
  }

  const elimina = async (id: number) => {
    if (!window.confirm('Disattivare questo template?')) return
    try {
      await rapportinoTemplatesApi.delete(id)
      if (selectedId === id) { setTemplate(null); setSelectedId(null) }
      await loadTemplates()
      toast.success('Template disattivato')
    } catch {
      toast.error('Errore nella disattivazione')
    }
  }

  const selectedCampo = template?.campi.find(c => c.id === selectedCampoId) ?? null

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="rpt-editor">

      {/* SIDEBAR SINISTRA */}
      <aside className="rpt-sidebar">
        <div className="rpt-sidebar-header">
          <h3>Template Rapportini</h3>
          <button className="btn-primary btn-sm" onClick={nuovoTemplate}>+ Nuovo</button>
        </div>
        <div className="rpt-template-list">
          {(templates ?? []).map(t => (
            <div
              key={t.id}
              className={`rpt-template-item${selectedId === t.id ? ' active' : ''}${!t.attivo ? ' inactive' : ''}`}
              onClick={() => selectTemplate(t.id)}
            >
              <div className="rpt-template-item-nome">{t.nome}</div>
              <div className="rpt-template-item-meta">
                {t.committente_nome
                  ? <span className="badge-committente">{t.committente_nome}</span>
                  : <span className="badge-global">Globale</span>}
                <span className="badge-campi">{t.num_campi} campi</span>
                {!t.attivo && <span className="badge-inactive">Inattivo</span>}
              </div>
              <div className="rpt-template-item-actions" onClick={e => e.stopPropagation()}>
                <button title="Duplica" onClick={() => duplica(t.id)}>⧉</button>
                <button title="Disattiva" onClick={() => elimina(t.id)}>🗑</button>
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="rpt-empty">Nessun template. Creane uno.</div>
          )}
        </div>
      </aside>

      {/* AREA PRINCIPALE */}
      {template ? (
        <div className="rpt-main">

          <div className="rpt-main-header">
            <div className="rpt-meta-row">
              <input
                className="rpt-nome-input"
                value={template.nome}
                onChange={e => { setTemplate(t => t ? { ...t, nome: e.target.value } : t); setDirty(true) }}
                placeholder="Nome template"
              />
              <select
                value={template.committente_id ?? ''}
                onChange={e => {
                  setTemplate(t => t ? { ...t, committente_id: e.target.value ? Number(e.target.value) : null } : t)
                  setDirty(true)
                }}
              >
                <option value="">— Template Globale (default) —</option>
                {(committenti ?? []).map(c => (
                  <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
                ))}
              </select>
              <label className="rpt-attivo-toggle">
                <input
                  type="checkbox"
                  checked={template.attivo}
                  onChange={e => { setTemplate(t => t ? { ...t, attivo: e.target.checked } : t); setDirty(true) }}
                />
                Attivo
              </label>
            </div>
            <div className="rpt-header-actions">
              <div className="rpt-tabs">
                <button className={tab === 'editor' ? 'active' : ''} onClick={() => setTab('editor')}>Editor</button>
                <button className={tab === 'anteprima' ? 'active' : ''} onClick={() => setTab('anteprima')}>Anteprima</button>
              </div>
              {dirty && <span className="rpt-dirty-badge">● Non salvato</span>}
              <button className="btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>

          {tab === 'editor' ? (
            <div className="rpt-editor-body">

              {/* CANVAS CAMPI */}
              <div className="rpt-canvas">
                <div className="rpt-canvas-inner">
                  {template.campi.length === 0 && (
                    <div className="rpt-canvas-empty">
                      Aggiungi campi dalla palette →
                    </div>
                  )}
                  {template.campi.map((campo, index) => (
                    <div
                      key={campo.id}
                      className={[
                        'rpt-campo-row',
                        campo.larghezza === 'half' ? 'half' : 'full',
                        selectedCampoId === campo.id ? 'selected' : '',
                        dragOverIndex === index ? 'drag-over' : '',
                        draggedIndex === index ? 'dragging' : '',
                      ].filter(Boolean).join(' ')}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={e => handleDragOver(e, index)}
                      onDrop={() => handleDrop(index)}
                      onDragEnd={() => { setDraggedIndex(null); setDragOverIndex(null) }}
                      onClick={() => setSelectedCampoId(campo.id)}
                    >
                      <span className="rpt-drag-handle">⠿</span>
                      <span className="rpt-campo-icona">
                        {TIPI_CAMPO.find(t => t.tipo === campo.tipo)?.icona}
                      </span>
                      <div className="rpt-campo-info">
                        <span className="rpt-campo-etichetta">{campo.etichetta}</span>
                        <span className="rpt-campo-tipo">{campo.tipo}</span>
                        {campo.obbligatorio && <span className="badge-req">Obbligatorio</span>}
                      </div>
                      <button
                        className="rpt-rimuovi"
                        onClick={e => { e.stopPropagation(); rimuoviCampo(campo.id) }}
                        title="Rimuovi"
                      >×</button>
                    </div>
                  ))}
                </div>

                {/* PALETTE TIPI */}
                <div className="rpt-palette">
                  <div className="rpt-palette-title">Aggiungi campo</div>
                  {TIPI_CAMPO.map(({ tipo, icona, label }) => (
                    <button
                      key={tipo}
                      className="rpt-palette-btn"
                      onClick={() => aggiungiCampo(tipo as CampoTemplate['tipo'])}
                      title={`Aggiungi ${label}`}
                    >
                      <span>{icona}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* PANNELLO PROPRIETÀ */}
              {selectedCampo && (
                <Proprieta
                  campo={selectedCampo}
                  onChange={patch => updateCampo(selectedCampo.id, patch)}
                />
              )}

            </div>
          ) : (
            <AnteprimaRapportino campi={template.campi} />
          )}
        </div>
      ) : (
        <div className="rpt-placeholder">
          {loading ? 'Caricamento...' : 'Seleziona un template o creane uno nuovo.'}
        </div>
      )}
    </div>
  )
}

// ── PANNELLO PROPRIETÀ ────────────────────────────────────────────────────────
function Proprieta({ campo, onChange }: { campo: CampoTemplate; onChange: (p: Partial<CampoTemplate>) => void }) {
  return (
    <div className="rpt-proprieta">
      <h4>Proprietà campo</h4>

      <label>Etichetta
        <input value={campo.etichetta} onChange={e => onChange({ etichetta: e.target.value })} />
      </label>

      <label>Larghezza
        <select value={campo.larghezza} onChange={e => onChange({ larghezza: e.target.value as 'full' | 'half' })}>
          <option value="full">Intera riga</option>
          <option value="half">Mezza riga</option>
        </select>
      </label>

      <label className="rpt-checkbox-row">
        <input
          type="checkbox"
          checked={campo.obbligatorio}
          onChange={e => onChange({ obbligatorio: e.target.checked })}
        />
        Obbligatorio
      </label>

      {(campo.tipo === 'testo' || campo.tipo === 'textarea' || campo.tipo === 'numero') && (
        <label>Placeholder
          <input value={campo.placeholder ?? ''} onChange={e => onChange({ placeholder: e.target.value })} />
        </label>
      )}

      {campo.tipo === 'foto' && (
        <label>Max foto
          <input
            type="number" min={1} max={6}
            value={campo.max_foto ?? 4}
            onChange={e => onChange({ max_foto: Number(e.target.value) })}
          />
        </label>
      )}

      {campo.tipo === 'select' && (
        <>
          <label className="rpt-checkbox-row">
            <input type="checkbox" checked={!!campo.scelte_multiple}
              onChange={e => onChange({ scelte_multiple: e.target.checked })} />
            Scelte multiple (checkbox group)
          </label>
          <div className="rpt-opzioni">
            <label>Opzioni (una per riga)</label>
            <textarea
              rows={5}
              value={(campo.opzioni ?? []).join('\n')}
              onChange={e => onChange({ opzioni: e.target.value.split('\n') })}
              onBlur={e => onChange({ opzioni: e.target.value.split('\n').filter(Boolean) })}
            />
          </div>
        </>
      )}

      <label>Note admin (non visibili al tecnico)
        <textarea
          rows={2}
          value={campo.note_admin ?? ''}
          onChange={e => onChange({ note_admin: e.target.value })}
        />
      </label>
    </div>
  )
}

// ── ANTEPRIMA RAPPORTINO ──────────────────────────────────────────────────────
function AnteprimaRapportino({ campi }: { campi: CampoTemplate[] }) {
  return (
    <div className="rpt-anteprima">
      <div className="rpt-anteprima-device">
        <div className="rpt-anteprima-title">Anteprima form tecnico</div>
        <div className="rpt-anteprima-form">
          {campi.map(campo => (
            <div key={campo.id} className={`rpt-preview-campo ${campo.larghezza}`}>
              {campo.tipo === 'separatore' ? (
                <div className="rpt-preview-separatore">
                  <hr /><span>{campo.etichetta}</span>
                </div>
              ) : (
                <>
                  <label className="rpt-preview-label">
                    {campo.etichetta}
                    {campo.obbligatorio && <span className="req-star">*</span>}
                  </label>
                  {campo.tipo === 'testo' && <input disabled placeholder={campo.placeholder} />}
                  {campo.tipo === 'textarea' && <textarea disabled placeholder={campo.placeholder} rows={3} />}
                  {campo.tipo === 'numero' && <input type="number" disabled placeholder={campo.placeholder} />}
                  {campo.tipo === 'data' && <input type="date" disabled />}
                  {campo.tipo === 'ora' && <input type="time" disabled />}
                  {campo.tipo === 'checkbox' && <input type="checkbox" disabled />}
                  {campo.tipo === 'select' && (
                    <select disabled>
                      <option>Seleziona...</option>
                      {(campo.opzioni ?? []).map(o => <option key={o}>{o}</option>)}
                    </select>
                  )}
                  {campo.tipo === 'foto' && (
                    <div className="rpt-preview-foto">📷 Upload fino a {campo.max_foto ?? 4} foto</div>
                  )}
                  {campo.tipo === 'firma' && (
                    <div className="rpt-preview-firma">✍️ Area firma</div>
                  )}
                  {campo.tipo === 'materiali' && (
                    <div className="rpt-preview-materiali">📦 Selezione kit/materiali</div>
                  )}
                </>
              )}
            </div>
          ))}
          {campi.length === 0 && <div className="rpt-preview-empty">Nessun campo nel template</div>}
        </div>
      </div>
    </div>
  )
}
